import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { useTheme } from '@/lib/theme'

interface ThreeBackgroundProps {
  /** Extra classes for the wrapper (positioning, z-index). */
  className?: string
  /** Animation speed multiplier. Default 1. */
  speed?: number
  /** Number of floating boxes. Default 8. */
  boxCount?: number
  /** Render the 3D canvas on screens < 768px too. Default false (gradient fallback). */
  enableOnMobile?: boolean
}

/** Reads a `--p-*` CSS var ("R G B" channels) off <html> and returns a THREE.Color. */
function cssVarColor(name: string, fallback: number): THREE.Color {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  const parts = raw.split(/\s+/).map(Number)
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    return new THREE.Color(parts[0] / 255, parts[1] / 255, parts[2] / 255)
  }
  return new THREE.Color(fallback)
}

export default function ThreeBackground({
  className = '',
  speed = 1,
  boxCount = 13,
  enableOnMobile = false,
}: ThreeBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  // theme + colorKey drive a re-init so the boxes pick up new palette/dark-mode colors.
  const { theme, colorKey } = useTheme()

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  )

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    if (isMobile && !enableOnMobile) return
    // Respect users who prefer reduced motion — skip the animation entirely.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const width = container.clientWidth || window.innerWidth
    const height = container.clientHeight || window.innerHeight

    const scene = new THREE.Scene()
    // Orthographic (not perspective) so every cube keeps identical proportions no
    // matter where it sits — no edge-of-frame stretching. FRUSTUM is the half-height
    // of the visible area in world units.
    const FRUSTUM = 12
    const camera = new THREE.OrthographicCamera(
      -FRUSTUM * (width / height),
      FRUSTUM * (width / height),
      FRUSTUM,
      -FRUSTUM,
      0.1,
      1000,
    )
    camera.position.z = 30

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    })
    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setClearColor(0x000000, 0) // transparent
    container.appendChild(renderer.domElement)

    // Palette-aware colors pulled from the live CSS vars.
    const boxColor = cssVarColor('--p-500', 0x06b6d4)
    const emissive = cssVarColor(theme === 'dark' ? '--p-700' : '--p-300', 0x2a5f80)

    const geometry = new THREE.BoxGeometry(2, 2, 2)
    // flatShading shades each face independently so the cube's facets read clearly.
    const material = new THREE.MeshPhongMaterial({
      color: boxColor,
      emissive,
      flatShading: true,
      transparent: true,
      opacity: theme === 'dark' ? 0.45 : 0.35,
    })

    const boxes: THREE.Object3D[] = []
    // Spread boxes wide across the (short, wide) hero and push them out of the
    // dead-center: each box gets a minimum horizontal offset so they fan to the sides.
    const aspect = width / height
    const spreadX = FRUSTUM * Math.max(aspect, 1)
    for (let i = 0; i < boxCount; i++) {
      const box = new THREE.Mesh(geometry, material)
      // Uniform scale → still a perfect cube, but each cube is a different size.
      box.scale.setScalar(0.45 + Math.random() * 1.4)
      const side = i % 2 === 0 ? 1 : -1 // alternate left/right so both sides fill
      const x = side * (spreadX * 0.2 + Math.random() * spreadX * 0.6)
      box.position.set(x, (Math.random() - 0.5) * 16, (Math.random() - 0.5) * 18)
      box.rotation.set(Math.random(), Math.random(), Math.random())
      scene.add(box)
      boxes.push(box)
    }

    scene.add(new THREE.AmbientLight(0xffffff, 0.5))

    const light1 = new THREE.PointLight(boxColor, 1)
    light1.position.set(10, 10, 10)
    scene.add(light1)

    const light2 = new THREE.PointLight(cssVarColor('--p-400', 0xff64e8), 0.8)
    light2.position.set(-10, -10, 10)
    scene.add(light2)

    let frameId = 0
    const animate = () => {
      frameId = requestAnimationFrame(animate)
      const t = Date.now() * 0.0005 * speed
      boxes.forEach((box, i) => {
        const dir = i % 2 === 0 ? 1 : -1
        box.rotation.x += 0.001 * speed * dir
        box.rotation.y += 0.0015 * speed * dir
        box.position.y += Math.sin(t + i) * 0.01
      })
      renderer.render(scene, camera)
    }

    if (reduceMotion) {
      renderer.render(scene, camera) // single static frame
    } else {
      animate()
    }

    const resize = () => {
      const w = container.clientWidth || window.innerWidth
      const h = container.clientHeight || window.innerHeight
      const a = w / h
      camera.left = -FRUSTUM * a
      camera.right = FRUSTUM * a
      camera.top = FRUSTUM
      camera.bottom = -FRUSTUM
      camera.updateProjectionMatrix()
      renderer.setSize(w, h)
    }
    const ro = new ResizeObserver(resize)
    ro.observe(container)

    return () => {
      cancelAnimationFrame(frameId)
      ro.disconnect()
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement)
      }
      geometry.dispose()
      material.dispose()
      renderer.dispose()
    }
  }, [isMobile, enableOnMobile, speed, boxCount, theme, colorKey])

  // Mobile (or 3D disabled): cheap gradient fallback that still respects the palette.
  if (isMobile && !enableOnMobile) {
    return (
      <div
        className={className}
        aria-hidden="true"
        style={{
          background:
            'linear-gradient(135deg, rgb(var(--p-950)) 0%, rgb(var(--p-800)) 100%)',
          pointerEvents: 'none',
        }}
      />
    )
  }

  return (
    <div
      ref={containerRef}
      className={className}
      aria-hidden="true"
      style={{ pointerEvents: 'none', overflow: 'hidden' }}
    />
  )
}
